import type { ActionReducerMapBuilder, PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { IReduxRequestNode } from "@litres-libs/types";
import type { IArtsReviewsState } from "./artsReviews.types";
import type { IReview, IReviewsResponse } from "../../reviews/reviews.types";
import { Request } from "../../../../request/Request";
import { getGlobalHeaders } from "../../../browser/browserSlice";
import type { RootState } from "../../../../store";

export const addArtReviewLike = createAsyncThunk(
  "global/arts/reviews/addArtReviewLike",
  async (reviewID: number, { getState }) => {
    const response = new Request.Foundation<null, IReviewsResponse>({
      url: `/api/reviews/${reviewID}/likes`,
      provider: {
        id: "ADD_ART_REVIEW_LIKE",
      },
      request: {
        method: "PUT",
      },
      headers: {
        ...getGlobalHeaders(getState() as RootState),
      },
    });

    return await response.fetch();
  }
);

export const removeArtReviewLike = createAsyncThunk(
  "global/arts/reviews/removeArtReviewLike",
  async (reviewID: number, { getState }) => {
    const response = new Request.Foundation<null, IReviewsResponse>({
      url: `/api/reviews/${reviewID}/likes`,
      provider: {
        id: "REMOVE_ART_REVIEW_LIKE",
      },
      request: {
        method: "DELETE",
      },
      headers: {
        ...getGlobalHeaders(getState() as RootState),
      },
    });

    return await response.fetch();
  }
);

export const addArtReviewDislike = createAsyncThunk(
  "global/arts/reviews/addReviewDislike",
  async (reviewID: number, { getState }) => {
    const response = new Request.Foundation<null, IReviewsResponse>({
      url: `/api/reviews/${reviewID}/dislikes`,
      provider: {
        id: "ADD_ART_REVIEW_DISLIKE",
      },
      request: {
        method: "PUT",
      },
      headers: {
        ...getGlobalHeaders(getState() as RootState),
      },
    });

    return await response.fetch();
  }
);

export const removeArtReviewDislike = createAsyncThunk(
  "global/arts/reviews/removeArtReviewDislike",
  async (reviewID: number, { getState }) => {
    const response = new Request.Foundation<null, IReviewsResponse>({
      url: `/api/reviews/${reviewID}/dislikes`,
      provider: {
        id: "REMOVE_ART_REVIEW_DISLIKE",
      },
      request: {
        method: "DELETE",
      },
      headers: {
        ...getGlobalHeaders(getState() as RootState),
      },
    });

    return await response.fetch();
  }
);

interface IActionMeta {
  arg: number;
  requestId: string;
  requestStatus: string;
}

export const artsReviewLikesCases = (
  builder: ActionReducerMapBuilder<IArtsReviewsState>
): void => {
  // Загрузка
  builder.addCase(
    addArtReviewLike.pending,
    (
      state: IArtsReviewsState,
      action: PayloadAction<undefined, string, IActionMeta>
    ) => {
      const reviewId = action.meta.arg;
      if (typeof state.actions.addArtReviewLike[reviewId] === "undefined") {
        state.actions.addArtReviewLike[reviewId] = {};
      }
      state.actions.addArtReviewLike[reviewId].isLoading = true;
      state.actions.addArtReviewLike[reviewId].error = undefined;
    }
  );
  // Ответ
  builder.addCase(
    addArtReviewLike.fulfilled,
    (
      state: IArtsReviewsState,
      action: PayloadAction<
        IReduxRequestNode<null, IReviewsResponse>,
        string,
        IActionMeta
      >
    ) => {
      const reviewId = action.meta.arg;
      if (typeof state.actions.addArtReviewLike[reviewId] === "undefined") {
        state.actions.addArtReviewLike[reviewId] = {};
      }
      state.actions.addArtReviewLike[reviewId].isLoading = false;

      if (action.payload.error) {
        console.error("Ошибка запроса addArtReviewLike", action.payload.error);
        state.actions.addArtReviewLike[reviewId].error = action.payload.error;
        return;
      }

      updateReviewState(state, reviewId, action.payload.response);
    }
  );

  // Загрузка
  builder.addCase(
    removeArtReviewLike.pending,
    (
      state: IArtsReviewsState,
      action: PayloadAction<undefined, string, IActionMeta>
    ) => {
      const reviewId = action.meta.arg;
      if (typeof state.actions.removeArtReviewLike[reviewId] === "undefined") {
        state.actions.removeArtReviewLike[reviewId] = {};
      }
      state.actions.removeArtReviewLike[reviewId].isLoading = true;
      state.actions.removeArtReviewLike[reviewId].error = undefined;
    }
  );
  // Ответ
  builder.addCase(
    removeArtReviewLike.fulfilled,
    (
      state: IArtsReviewsState,
      action: PayloadAction<
        IReduxRequestNode<null, IReviewsResponse>,
        string,
        IActionMeta
      >
    ) => {
      const reviewId = action.meta.arg;
      if (typeof state.actions.removeArtReviewLike[reviewId] === "undefined") {
        state.actions.removeArtReviewLike[reviewId] = {};
      }
      state.actions.removeArtReviewLike[reviewId].isLoading = false;

      if (action.payload.error) {
        console.error(
          "Ошибка запроса removeArtReviewLike",
          action.payload.error
        );
        state.actions.removeArtReviewLike[reviewId].error =
          action.payload.error;
        return;
      }

      updateReviewState(state, reviewId, action.payload.response);
    }
  );
};

export const artsReviewDislikesCases = (
  builder: ActionReducerMapBuilder<IArtsReviewsState>
): void => {
  // Загрузка
  builder.addCase(
    addArtReviewDislike.pending,
    (
      state: IArtsReviewsState,
      action: PayloadAction<undefined, string, IActionMeta>
    ) => {
      const reviewId = action.meta.arg;
      if (typeof state.actions.addArtReviewDislike[reviewId] === "undefined") {
        state.actions.addArtReviewDislike[reviewId] = {};
      }
      state.actions.addArtReviewDislike[reviewId].isLoading = true;
      state.actions.addArtReviewDislike[reviewId].error = undefined;
    }
  );
  // Ответ
  builder.addCase(
    addArtReviewDislike.fulfilled,
    (
      state: IArtsReviewsState,
      action: PayloadAction<
        IReduxRequestNode<null, IReviewsResponse>,
        string,
        IActionMeta
      >
    ) => {
      const reviewId = action.meta.arg;
      if (typeof state.actions.addArtReviewDislike[reviewId] === "undefined") {
        state.actions.addArtReviewDislike[reviewId] = {};
      }
      state.actions.addArtReviewDislike[reviewId].isLoading = false;

      if (action.payload.error) {
        console.error(
          "Ошибка запроса addArtReviewDislike",
          action.payload.error
        );
        state.actions.addArtReviewDislike[reviewId].error =
          action.payload.error;
        return;
      }

      updateReviewState(state, reviewId, action.payload.response);
    }
  );

  // Загрузка
  builder.addCase(
    removeArtReviewDislike.pending,
    (
      state: IArtsReviewsState,
      action: PayloadAction<undefined, string, IActionMeta>
    ) => {
      const reviewId = action.meta.arg;
      if (
        typeof state.actions.removeArtReviewDislike[reviewId] === "undefined"
      ) {
        state.actions.removeArtReviewDislike[reviewId] = {};
      }
      state.actions.removeArtReviewDislike[reviewId].isLoading = true;
      state.actions.removeArtReviewDislike[reviewId].error = undefined;
    }
  );
  // Ответ
  builder.addCase(
    removeArtReviewDislike.fulfilled,
    (
      state: IArtsReviewsState,
      action: PayloadAction<
        IReduxRequestNode<null, IReviewsResponse>,
        string,
        IActionMeta
      >
    ) => {
      const reviewId = action.meta.arg;
      if (
        typeof state.actions.removeArtReviewDislike[reviewId] === "undefined"
      ) {
        state.actions.removeArtReviewDislike[reviewId] = {};
      }
      state.actions.removeArtReviewDislike[reviewId].isLoading = false;

      if (action.payload.error) {
        console.error(
          "Ошибка запроса removeArtReviewDislike",
          action.payload.error
        );
        state.actions.removeArtReviewDislike[reviewId].error =
          action.payload.error;
        return;
      }

      updateReviewState(state, reviewId, action.payload.response);
    }
  );
};

export const updateReviewState = (
  state: IArtsReviewsState,
  reviewId: unknown,
  response?: IReviewsResponse
): void => {
  if (typeof reviewId === "number") {
    const index = findReviewIndex(reviewId, state.list);
    if (
      index !== -1 &&
      response?.payload?.data &&
      !Array.isArray(response.payload.data)
    ) {
      state.list[index] = response.payload.data;
    }
  }
};

export const findReviewIndex = (reviewId: number, list: IReview[]): number => {
  let replies = [];

  for (let i = 0; i < list.length; i++) {
    if (list[i].id === reviewId) {
      return i;
    }
    if (Array.isArray(list[i].replies)) {
      replies = list[i].replies as IReview[];
      for (let j = 0; j < replies.length; j++) {
        if (replies[j].id === reviewId) {
          return i;
        }
      }
    }
  }

  return -1;
};
